import 'normalize.css/normalize.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import WS from 'webstudio/src/client/facade';
import { ObjectItemType } from 'webstudio/src/client/project_content_types';
import { PROJECT_STATUS } from '_data/constants';
import { getObjectLoadingProgress } from '_data/helpers/loadingProgress';
import { USER_ROLES, SHARING_TYPES } from '_dictionaries';
import { getTimeLeftString } from '_helpers/methods';
import { isNullable } from '_helpers/nullable';
import { addCloudPointerEvents, removeCloudPointerEvents } from '_helpers/pointerEvents/mainScene';
import { addUiEvents, removeUiEvent } from '_helpers/uiEvents';
import '../assets/styles/login/login.css';
import '../components/shared/svgSprite/loginSprite';

dayjs.extend(utc);

const ASSETS_API_PATH = '/api/frontend/guests';
let canvas = null;
let helpOpened = false;
let navigationOpened = true;

(() => {
  const scheme = window.matchMedia('(prefers-color-scheme)').matches;
  if (scheme) {
    const lightMode = window.matchMedia('(prefers-color-scheme: light)');
    const darkMode = window.matchMedia('(prefers-color-scheme: dark)');

    const favicons = document.querySelectorAll('link[rel="icon"]');
    const toggleTheme = e => {
      const darkModeOn = e.detail.colorScheme === 'dark';
      favicons.forEach(link => {
        link.href = darkModeOn ? link.dataset.hrefDark : link.dataset.hrefLight;
      });
    };
    darkMode.addListener(e => e.matches && toggleTheme({ detail: { colorScheme: 'dark' } }));
    lightMode.addListener(e => e.matches && toggleTheme({ detail: { colorScheme: 'light' } }));

    toggleTheme({ detail: { colorScheme: lightMode.matches ? 'light' : 'dark' } });

    window.addEventListener('colorschemechange', toggleTheme);
  }
})();

window.onbeforeunload = () => {
  removeUiEvent();
  if (canvas) {
    removeCloudPointerEvents(canvas);
  }
};

const getLeft = (eta, at) => eta * 1000 - (new Date().getTime() - new Date(at).getTime());

const initNavigation = () => {
  const link_navigation = document.getElementById('link_navigation');
  if (!link_navigation) {
    return;
  }
  const help = document.querySelector('.help');
  const help_menu = document.querySelector('.help__menu');
  const link_tabs = document.querySelector('.link__tabs');
  const link_tabs_close = document.querySelector('.link__tabs-close');
  help.addEventListener('click', () => {
    helpOpened = !helpOpened;
    help_menu.style.display = helpOpened ? 'block' : 'none';
  });
  help_menu.addEventListener('click', () => {
    navigationOpened = true;
    link_tabs.style.display = 'flex';
  });
  link_tabs_close.addEventListener('click', () => {
    navigationOpened = false;
    link_tabs.style.display = 'none';
  });
  document.addEventListener('click', e => {
    if (e.target !== help) {
      helpOpened = false;
      help_menu.style.display = 'none';
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  canvas = document.getElementById('canvas');

  addUiEvents();
  addCloudPointerEvents({ canvas });
  const { protocol, host, pathname, search } = window.location;
  const _projectUrl = `${protocol}//${host}/api/frontend/guests/projects/${pathname.replace(
    '/projects/',
    ''
  )}${search}`;

  canvas.classList.add('loaded');
  let sharedObjects;
  let shareType;
  let sharedObject;
  const settedVisibilityIds = [];

  const _fetchProjectData = () =>
    fetch(_projectUrl)
      .then(response => response.json())
      .then(({ project }) => project);

  fetch(_projectUrl)
    .then(response => {
      if (response.ok) {
        document.getElementById('progress').style.display = 'none';
        return response.json();
      }
      window.location.href = '/404';
      return false;
    })
    .then(({ project }) => {
      const {
        assets_path,
        project_base,
        shared_objects,
        user_role,
        sharing_type,
        eta_for_convert,
        conversion_started_at,
        status,
      } = project;

      sharedObjects = shared_objects;
      shareType = sharing_type;

      document.getElementById('status').innerText =
        project.status === PROJECT_STATUS.uploading ? 'Uploading' : 'Converting';

      const accessType = document.getElementById('access_role');
      const welcomeText = document.getElementById('welcome_text');
      const welcomeInfo = document.getElementById('welcome_info');

      switch (user_role) {
        case USER_ROLES['Only view']:
          accessType.innerHTML = 'view only';
          welcomeText.innerHTML = 'Welcome to ArtecCloud! Log in or Sign up to leave a comment.';
          break;
        case USER_ROLES['View and download']:
          accessType.innerHTML = 'view and download';
          welcomeText.innerHTML = 'Welcome to ArtecCloud! Login or Sign up to leave a comment or download.';
          break;
        default:
          accessType.innerHTML = 'edit';
          welcomeText.innerHTML = 'Welcome to ArtecCloud! Login or Sign up to edit, leave a comment or download.';
      }

      welcomeInfo.style.opacity = '1';

      if (status === PROJECT_STATUS.processed) {
        const path = `${ASSETS_API_PATH}${assets_path}${project_base}${search}`;

        initNavigation();

        WS.init({ canvas })
          .then(() => WS.setScanMode({ mode: WS.SCM_SURFELS_HIGH }))
          .then(() => {
            WS.setBackFaceMode({ mode: WS.BM_TRANSPARENT });
            WS.setBackgroundColor({ color: { r: 232 / 255, g: 233 / 255, b: 236 / 255 } });
            WS.setGridColor({ color: { r: 1, g: 1, b: 1 } });
            WS.setGridVisibility({ isVisible: false });
            WS.showGridAxes({ show: false });
            return WS.loadProject({ path });
          })
          .then(() => {})
          .catch(() => {
            console.log('init interrupted');
          });
        return;
      }

      if ([PROJECT_STATUS.failed_to_upload, PROJECT_STATUS.failed_to_convert].indexOf(status) !== -1) {
        document.getElementById('canvas').style.opacity = 0;
        document.querySelector('div.loading.error').style.display = 'flex';
        return;
      }

      document.getElementById('canvas').style.opacity = 0;
      document.querySelector('div.loading.process').style.display = 'flex';

      const script = document.createElement('script');

      script.src = '/icosaedron.js';
      script.async = true;

      document.body.appendChild(script);

      let leftText;
      let processingEta;
      let left;

      if (project.status !== PROJECT_STATUS.processing) {
        console.log('not processing');
        setInterval(async () => {
          const _project = await _fetchProjectData({ slug: `${project.id}` });
          if (
            [PROJECT_STATUS.processed, PROJECT_STATUS.failed_to_upload, PROJECT_STATUS.failed_to_convert].indexOf(
              _project.status
            ) !== -1
          ) {
            window.location.reload();
          }
          document.getElementById('status').innerText =
            _project.status === PROJECT_STATUS.uploading ? 'Uploading' : 'Converting';

          console.log('not processing 1', _project.status, processingEta);
          if (_project.status === PROJECT_STATUS.processing && !processingEta) {
            console.log('not processing 2');
            processingEta = _project.eta_for_convert;
            left = getLeft(_project.eta_for_convert, _project.conversion_started_at);
            setInterval(() => {
              left -= 1000;
              leftText = getTimeLeftString(left, '', 'left');
              document.getElementById('left').innerText = leftText;
              console.log('not processing 3', leftText, '!');
            }, 1000);
          }
        }, 3000);
      } else {
        left = getLeft(eta_for_convert, conversion_started_at);
        setInterval(() => {
          left -= 1000;
          leftText = getTimeLeftString(left, '', 'left');
          document.getElementById('left').innerText = leftText;
          console.log('processing 1', leftText, '!!!!');
        }, 1000);
        document.getElementById('status').innerText = 'Converting';
        setTimeout(() => {
          console.log(' processing retimeour');
          setInterval(async () => {
            const _project = await _fetchProjectData({ slug: `${project.id}` });
            if (
              [PROJECT_STATUS.processed, PROJECT_STATUS.failed_to_upload, PROJECT_STATUS.failed_to_convert].indexOf(
                _project.status
              ) !== -1
            ) {
              window.location.reload();
            }
            console.log(' processing reload');
          }, 3000);
        }, left);
      }
    });

  WS.on({
    event: 'error',
    callback: state => {
      console.log(state);
    },
  });

  WS.on({
    event: 'projectContent',
    callback: content => {
      const projectContent = content.clone().objects;
      if (projectContent && sharedObjects) {
        if (isNullable(sharedObject)) {
          sharedObject = projectContent.find(object => object.uuid === sharedObjects[0]);
        }

        const allProjectContentIDs = projectContent
          .filter(({ id }) => settedVisibilityIds.indexOf(id) === -1)
          .map(({ id, uuid, visible }) => ({ id, uuid, visible }));

        const visibilityPromises = [];

        allProjectContentIDs.forEach(_ => {
          if (
            (shareType === SHARING_TYPES.Project || sharedObjects.indexOf(_.uuid) !== -1) &&
            _.type !== ObjectItemType.Group
          ) {
            visibilityPromises.push(
              WS.setObjectVisibility({
                objectID: _.id,
                visible: true,
              })
            );
            settedVisibilityIds.push(_.id);
          } else if (_.visible && sharedObjects.indexOf(_.uuid) === -1 && _.type !== ObjectItemType.Group) {
            visibilityPromises.push(
              WS.setObjectVisibility({
                objectID: _.id,
                visible: false,
              })
            );
            settedVisibilityIds.push(_.id);
          }
        });

        Promise.all(visibilityPromises).then(() => {
          const _params = {
            minx: 10,
            miny: 55,
            maxx: window.innerWidth - 10,
            maxy: window.innerHeight - 10,
          };
          WS.fitCamera(_params);
        });
      }
    },
  });

  WS.on({
    event: 'loadingProgress',
    callback: loadingProgress => {
      if (sharedObject) {
        const loadingProgressLevelsObject = loadingProgress[sharedObject.id];
        const geometryProgress =
          loadingProgressLevelsObject &&
          getObjectLoadingProgress({ loadingProgressLevelsObject, kind: sharedObject.type });

        if (geometryProgress === 100) {
          document.getElementById('progressObjects').style.display = 'none';
        } else if (geometryProgress) {
          document.getElementById('overall').style.width = `${geometryProgress}%`;
        }
      }
    },
  });
});
